import { useState } from 'react';
import { createPortal } from 'react-dom';
import { LeafletMap, MapPlace, MapUser } from './LeafletMap';

/**
 * "Mapa al estilo Uber": hoja flotante con el mapa, tu posición con su margen de error y los lugares
 * autorizados con su radio. Se puede minimizar a una pastilla (queda flotando sobre la barra inferior)
 * sin perder el estado. En el celular sube desde abajo; en pantallas grandes flota a la derecha.
 */
export function LocationMapSheet({ open, onClose, places, user, summary, tone }: { open: boolean; onClose: () => void; places: MapPlace[]; user: MapUser | null; summary: string; tone: 'ok' | 'out' | 'neutral' }) {
  const [minimized, setMinimized] = useState(false);
  if (!open) return null;
  const dot = tone === 'ok' ? 'bg-green-500' : tone === 'out' ? 'bg-orange-500' : 'bg-sky-500';
  const head = tone === 'ok' ? 'En rango' : tone === 'out' ? 'Fuera de rango' : 'Tu ubicación';

  if (minimized) {
    return createPortal(
      <button
        type="button"
        onClick={() => setMinimized(false)}
        className="fixed bottom-24 right-4 z-[60] flex max-w-[calc(100vw-2rem)] items-center gap-2 rounded-full border border-white/10 bg-[#0f172a] px-4 py-3 text-left text-sm font-semibold text-white shadow-float"
        aria-label="Abrir mapa"
      >
        <span className={['h-3 w-3 shrink-0 rounded-full', dot].join(' ')} />
        <span className="truncate">Mapa · {head}</span>
        <span aria-hidden>▲</span>
      </button>,
      document.body
    );
  }

  return createPortal(
    <div className="fixed inset-x-0 bottom-0 z-[60] sm:inset-x-auto sm:bottom-6 sm:right-6 sm:w-[26rem]">
      <div className="flex max-h-[85vh] flex-col overflow-hidden rounded-t-3xl border border-ink-100 bg-surface shadow-float sm:rounded-3xl">
        <div className="flex items-center gap-3 px-4 pb-2 pt-3">
          <span className={['h-3 w-3 shrink-0 rounded-full', dot].join(' ')} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-ink-900">{head}</p>
            <p className="break-words text-xs text-ink-500">{summary}</p>
          </div>
          <button type="button" onClick={() => setMinimized(true)} className="flex h-9 w-9 items-center justify-center rounded-lg text-ink-500 hover:bg-ink-50" aria-label="Minimizar mapa">▼</button>
          <button type="button" onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-lg text-ink-500 hover:bg-ink-50" aria-label="Cerrar mapa">✕</button>
        </div>
        <LeafletMap places={places} user={user} className="mx-3 h-[52vh] min-h-[260px] sm:h-[22rem]" />
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 pb-4 pt-3 text-[11px] text-ink-500">
          <span className="flex items-center gap-1.5"><i className="inline-block h-2.5 w-2.5 rounded bg-blue-600" /> Oficina / sede</span>
          <span className="flex items-center gap-1.5"><i className="inline-block h-2.5 w-2.5 rounded bg-violet-600" /> Remoto</span>
          <span className="flex items-center gap-1.5"><i className="inline-block h-2.5 w-2.5 rounded-full bg-green-500" /> Tú, en rango</span>
          <span className="flex items-center gap-1.5"><i className="inline-block h-2.5 w-2.5 rounded-full bg-orange-500" /> Tú, fuera</span>
          <span>El círculo claro a tu alrededor es el margen de error del GPS.</span>
        </div>
      </div>
    </div>,
    document.body
  );
}
